<script setup lang="ts">
import ResourceTooltipContent from './ResourceTooltipContent.vue';
import type { DashboardResource } from '@/types/planetDashboard';

/**
 * Chip d'une ressource dans la barre header : icone + montant (selon le
 * comportement EResourceCategory) + fleche de tendance, avec popover riche
 * (ResourceTooltipContent) au survol. Taille FIXE et identique pour toutes les
 * chips. La couleur d'accent (bordure/fond) depend du groupe d'usage.
 *
 * IMPORTANT : les styles vivent ICI (scoped) car le composant est utilise
 * comme enfant d'AppHeader -> le scoped CSS du parent ne traverse pas.
 */
const props = defineProps<{
  resource: DashboardResource;
  /** Groupe d'usage -> couleur d'accent (materiaux/consommables/precieuses/vie). */
  group?: string;
  /** Mode fluide : la chip s'etire (flex 1) pour remplir la largeur, toutes
   *  egales entre elles, au lieu d'une largeur fixe. */
  fluid?: boolean;
}>();

void props;

function formatNumber(value: number): string {
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (Math.abs(value) >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}

// Flux (energie) : pas de stock -> on affiche le solde net /h ; sinon le dispo.
function resourceHeadline(r: DashboardResource): string {
  if ((r.category ?? 'Consommable') === 'Flux') {
    const v = r.productionPerHour;
    return `${v >= 0 ? '+' : ''}${formatNumber(v)}/h`;
  }
  return formatNumber(r.available);
}

// Echelle de police pour garder une largeur fixe meme avec un montant long.
function amountFontSize(text: string): string {
  const n = text.length;
  if (n <= 7) return '12px';
  if (n <= 9) return '11px';
  return '10px';
}

type ProdTrend = 'up' | 'down' | 'stable';
function productionTrend(r: DashboardResource): ProdTrend {
  const v = r.productionPerHour;
  if (v > 0.05) return 'up';
  if (v < -0.05) return 'down';
  return 'stable';
}
// La tendance (flèche) est purement visuelle : le détail chiffré + ton
// (hausse/déficit) est porté par ResourceTooltipContent affiché au survol.
const TREND_ARROW: Record<ProdTrend, string> = { up: '▲', down: '▼', stable: '=' };
</script>

<template>
  <AppInfoCard placement="bottom" :width="260">
    <div class="rc-chip" :class="[`rc-group-${group ?? 'consommables'}`, { 'rc-fluid': fluid }]">
      <img
        :src="`/images/thumb/64/resources/${resource.code}.png`"
        :alt="resource.label"
        class="rc-icon"
        loading="lazy"
        v-img-hide-on-error
      />
      <span
        class="rc-amount text-mono"
        :class="{ 'is-neg': (resource.category ?? 'Consommable') === 'Flux' && resource.productionPerHour < 0 }"
        :style="{ fontSize: amountFontSize(resourceHeadline(resource)) }"
      >{{ resourceHeadline(resource) }}</span>
      <span
        class="rc-trend"
        :class="`is-${productionTrend(resource)}`"
        aria-hidden="true"
      >{{ TREND_ARROW[productionTrend(resource)] }}</span>
    </div>
    <template #content>
      <ResourceTooltipContent :resource="resource" />
    </template>
  </AppInfoCard>
</template>

<style lang="scss" scoped>
// Chip : largeur + hauteur FIXES identiques pour toutes les ressources.
// --rc-accent = triplet RGB de la couleur d'usage (defaut cyan = consommables).
.rc-chip {
  --rc-accent: 0, 230, 255;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  flex: 0 0 auto;
  width: 112px;
  height: 26px;
  padding: 2px 6px 2px 2px;
  background: rgba(var(--rc-accent), 0.10);
  border: 1px solid rgba(var(--rc-accent), 0.38);
  border-radius: $radius-sm;
  font-size: $font-size-xs;
  overflow: hidden;
  cursor: help;

  &:hover { border-color: rgba(var(--rc-accent), 0.65); }

  // Mode fluide : s'etire pour remplir la largeur, toutes les chips d'une meme
  // ligne ont la meme largeur (flex 1 1 0). min-width:0 -> les chips peuvent
  // RETRECIR pour tenir sur une seule ligne (la barre est en nowrap) : sans ça,
  // un min-width forcerait le wrap/overflow à des points différents sur les 2
  // lignes -> séparateurs désalignés. max-width borne juste le cas "peu de chips".
  &.rc-fluid {
    flex: 1 1 0;
    width: auto;
    min-width: 0;
    max-width: 220px;
  }

  // Couleur d'accent par groupe d'usage.
  &.rc-group-materiaux  { --rc-accent: 251, 191, 36; }   // ambre (construction)
  &.rc-group-consommables { --rc-accent: 0, 230, 255; }  // cyan (carburants)
  &.rc-group-precieuses { --rc-accent: 168, 85, 247; }   // violet (gemmes)
  &.rc-group-vie        { --rc-accent: 34, 197, 94; }     // vert (vie)
}

.rc-icon {
  width: 22px;
  height: 22px;
  object-fit: contain;
  display: block;
  flex-shrink: 0;
}

.rc-amount {
  color: $text-primary;
  flex: 1 1 auto;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1;

  &.is-neg { color: $color-danger; }
}

.rc-trend {
  flex-shrink: 0;
  font-size: 9px;
  line-height: 1;
  margin-left: 1px;

  &.is-up { color: $color-success; }
  &.is-down { color: $color-danger; }
  &.is-stable { color: $text-muted; }
}
</style>
